/**
 * OrganiserFayreNewPage — /organiser/fayres/new
 *
 * Fayre creation form. All fields validated + whitelisted server-side.
 * Categories use the same SUPPLIER_CATEGORY_IDS as everywhere else so
 * the fayre-list filters plug in without extra mapping.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, CaretRight, CheckCircle } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_OPTIONS = [
  { id: 'dj', label: 'DJ / Band', emoji: '🎧' },
  { id: 'photographer', label: 'Photographer', emoji: '📷' },
  { id: 'videographer', label: 'Videographer', emoji: '🎥' },
  { id: 'photobooth', label: 'Photo Booth', emoji: '📸' },
  { id: 'caterer', label: 'Caterer', emoji: '🍽️' },
  { id: 'florist', label: 'Florist', emoji: '🌼' },
  { id: 'venue', label: 'Venue', emoji: '🏛️' },
  { id: 'cake_maker', label: 'Cake Maker', emoji: '🎂' },
  { id: 'transport', label: 'Transport', emoji: '🚗' },
  { id: 'decorator', label: 'Decorator', emoji: '🎨' },
  { id: 'wedding_planner', label: 'Wedding Planner', emoji: '✨' },
  { id: 'hair_makeup', label: 'Hair & Makeup', emoji: '💅' },
];

const todayIso = () => new Date().toISOString().slice(0, 10);

const OrganiserFayreNewPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [form, setForm] = useState({
    name: '',
    date: '',
    start_time: '12:00',
    end_time: '16:00',
    venue: '',
    city: '',
    postcode: '',
    description: '',
    image_url: '',
  });
  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    // Require organiser onboarding
    axios.get(`${API}/organisers/me`).then((r) => {
      if (!r.data.organiser) navigate('/organiser/onboard', { replace: true });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const toggleCat = (id) => {
    setCategories((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const submit = async () => {
    if (form.name.trim().length < 3) { toast.error('Fayre name is too short'); return; }
    if (!form.date) { toast.error('Pick a date'); return; }
    if (!categories.length) { toast.error('Pick at least one supplier category'); return; }
    if (!form.venue && !form.city) { toast.error('Add a venue or city'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/organisers/me/fayres`, {
        ...form,
        categories,
      });
      toast.success('Fayre published 🎉');
      navigate('/organiser/fayres');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create fayre');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1014] text-white pb-24" data-testid="organiser-new-fayre-page">
      <div className="sticky top-0 z-10 bg-[#0F1014]/95 backdrop-blur border-b border-white/5 px-5 py-3 flex items-center gap-3">
        <button onClick={() => navigate('/organiser/fayres')} className="text-white/60 hover:text-white" data-testid="new-fayre-back-btn">
          <ArrowLeft size={16} weight="bold" />
        </button>
        <p className="font-bold text-sm">New fayre</p>
      </div>

      <div className="max-w-xl mx-auto px-5 py-6 space-y-5">
        <Field label="Fayre name" required testid="field-name">
          <input
            type="text"
            value={form.name}
            onChange={(e) => set('name', e.target.value.slice(0, 120))}
            placeholder="e.g. Spring Wedding Fayre 2026"
            className="fld"
            data-testid="new-fayre-name"
          />
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Date" required testid="field-date">
            <input
              type="date"
              min={todayIso()}
              value={form.date}
              onChange={(e) => set('date', e.target.value)}
              className="fld"
              data-testid="new-fayre-date"
            />
          </Field>
          <Field label="Start" testid="field-start">
            <input
              type="time"
              value={form.start_time}
              onChange={(e) => set('start_time', e.target.value)}
              className="fld"
              data-testid="new-fayre-start"
            />
          </Field>
          <Field label="End" testid="field-end">
            <input
              type="time"
              value={form.end_time}
              onChange={(e) => set('end_time', e.target.value)}
              className="fld"
              data-testid="new-fayre-end"
            />
          </Field>
        </div>

        <Field label="Venue" testid="field-venue">
          <input
            type="text"
            value={form.venue}
            onChange={(e) => set('venue', e.target.value.slice(0, 200))}
            placeholder="e.g. The Old Barn"
            className="fld"
            data-testid="new-fayre-venue"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="City" testid="field-city">
            <input
              type="text"
              value={form.city}
              onChange={(e) => set('city', e.target.value.slice(0, 120))}
              placeholder="e.g. Oswestry"
              className="fld"
              data-testid="new-fayre-city"
            />
          </Field>
          <Field label="Postcode" testid="field-postcode">
            <input
              type="text"
              value={form.postcode}
              onChange={(e) => set('postcode', e.target.value.slice(0, 20))}
              placeholder="e.g. SY10 7JS"
              className="fld"
              data-testid="new-fayre-postcode"
            />
          </Field>
        </div>

        <div>
          <label className="block text-[10px] font-black uppercase tracking-wider text-white/50 mb-1.5">
            Supplier categories <span className="text-rose-400">*</span>
          </label>
          <div className="flex flex-wrap gap-1.5" data-testid="new-fayre-categories">
            {CATEGORY_OPTIONS.map((c) => {
              const active = categories.includes(c.id);
              return (
                <button
                  key={c.id}
                  onClick={() => toggleCat(c.id)}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-bold border transition ${
                    active
                      ? 'bg-[#B89762]/40/20 text-[#F4E9D2] border-[#B89762]/45/50'
                      : 'bg-white/[0.04] text-white/60 border-white/10 hover:bg-white/[0.08]'
                  }`}
                  data-testid={`new-fayre-cat-${c.id}`}
                  aria-pressed={active}
                >
                  {active && <CheckCircle size={11} weight="fill" className="inline -mt-0.5 mr-1 text-[#C29D52]" />}
                  {c.emoji} {c.label}
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-white/30 mt-1">Couples filter by these categories on the fayre page.</p>
        </div>

        <Field label="Description" testid="field-description">
          <textarea
            value={form.description}
            onChange={(e) => set('description', e.target.value.slice(0, 2000))}
            rows={4}
            placeholder="Tell couples what to expect at your fayre…"
            className="fld resize-none"
            data-testid="new-fayre-description"
          />
          <p className="text-[10px] text-white/30 mt-0.5">{form.description.length}/2000</p>
        </Field>

        <Field label="Image URL (optional)" testid="field-image">
          <input
            type="url"
            value={form.image_url}
            onChange={(e) => set('image_url', e.target.value.slice(0, 500))}
            placeholder="https://…"
            className="fld"
            data-testid="new-fayre-image"
          />
        </Field>

        <button
          onClick={submit}
          disabled={busy}
          className="w-full py-3 rounded-xl text-sm font-black text-white flex items-center justify-center gap-2 disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
          data-testid="new-fayre-submit"
        >
          {busy ? 'Publishing…' : 'Publish fayre'} <CaretRight size={12} weight="bold" />
        </button>

        <style>{`
          .fld { width: 100%; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.10);
            border-radius: 8px; padding: 10px 12px; font-size: 14px; color: #fff; outline: none; }
          .fld::placeholder { color: rgba(255,255,255,0.30); }
          .fld:focus { border-color: rgba(103,232,249,0.40); }
          .fld::-webkit-calendar-picker-indicator { filter: invert(1) brightness(0.8); }
        `}</style>
      </div>
    </div>
  );
};

const Field = ({ label, required, children, testid }) => (
  <div data-testid={testid}>
    <label className="block text-[10px] font-black uppercase tracking-wider text-white/50 mb-1.5">
      {label} {required && <span className="text-rose-400">*</span>}
    </label>
    {children}
  </div>
);

export default OrganiserFayreNewPage;
